import { IMappedAsset } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import { useEffect, useMemo, useState } from "react";

import { SundaeUtils } from "@sundaeswap/core/utilities";
import uniqBy from "lodash/uniqBy";
import { ADA_METADATA, SUNDAE_METADATA, readableAdaAssetId } from "../constants/cardano.constants";
import { PoolFragmentFragment } from "../gql/generated/stats2.sdk";
import { usePoolsByAssetIds } from "../gql/hooks/pools.query";
import { useOrderContext } from "../stores/OrderContext";
import { IAssetMetaData } from "../types/Asset.types";
import {
  getAssetWarning,
  normalizeAdaAssetIdsForGql,
  stripActiveAsset,
} from "../utils/assets.utils";
import { useAssetsWithBalance } from "./useAssetsWithBalance";
import { useOrderMode } from "./useExchangeMode";
import { useIsWalletConnected } from "./useIsWalletConnected";

interface IUseInputOutputAssetsArgs {
  forcePoolsForADA?: boolean;
  isDialog?: boolean;
  page?: number;
  pageSize?: number;
}

export const useInputOutputAssets = ({
  forcePoolsForADA = false,
  page,
  pageSize,
}: IUseInputOutputAssetsArgs) => {
  const {
    state: {
      assets: { given, taken },
    },
  } = useOrderContext();
  const assetIdForQuery: string[] = [];
  if (forcePoolsForADA) {
    assetIdForQuery.push(readableAdaAssetId);
  } else if (given) {
    assetIdForQuery.push(given.metadata.assetId);
  }

  const { searchAssetsBalance, assetsWithoutLP, balanceLoaded, balanceReloading } =
    useAssetsWithBalance();
  const isWalletConnected = useIsWalletConnected();
  const { isBuyOrder } = useOrderMode();
  const [poolsForInput, setPoolsForInput] = useState<Record<string, PoolFragmentFragment[]>>();
  const assetIds = useMemo(
    () =>
      [...assetIdForQuery].filter(
        (id) => SundaeUtils.isAdaAsset({ assetId: id, decimals: 6 }) || Boolean(id)
      ),
    [assetIdForQuery]
  );
  const {
    data,
    isFetching: isFetchingPoolsByAssetIds,
    isRefetching: isRefetchingPoolsByAssetIds,
  } = usePoolsByAssetIds({
    assetIds: normalizeAdaAssetIdsForGql(assetIds),
    pageSize,
    page,
  });

  useEffect(() => {
    if (data) setPoolsForInput((prev) => (page === 0 ? data.stats : { ...prev, ...data.stats }));
  }, [page, data]);

  const outputAssetList = useMemo(() => {
    let outputList: IMappedAsset<IAssetMetaData>[] = [];
    if (poolsForInput) {
      const outputAssets = Object.entries(poolsForInput).map(([_pairId, pools]) => {
        const { assetA: lpAssetA, assetB: lpAssetB } = pools[0]; // deepest liquidity

        // if assetA === lpAssetA, we want to select the paired asset, in this case, lpAssetB
        const pooledLPAsset = SundaeUtils.isAssetIdsEqual(
          lpAssetA.assetId,
          given?.metadata.assetId ?? ""
        )
          ? lpAssetB
          : lpAssetA;

        // if assetA is not selected yet, fall back to lpAssetB
        const asset = given?.metadata && !forcePoolsForADA ? pooledLPAsset : lpAssetB;

        const assetWithBalance = assetsWithoutLP.find(
          (assetWithBalance) => assetWithBalance?.metadata.assetId === asset.assetId
        );

        return (
          assetWithBalance ?? {
            metadata: { ...asset, warning: getAssetWarning(asset) },
            quantity: AssetAmount.fromValue(0n, asset),
          }
        );
      });

      // TODO: consider USD as well and place ADA on TOP
      outputList = outputAssets.sort((a, b) => (a.quantity.amount > b.quantity.amount ? -1 : 1));
    } else {
      outputList = assetsWithoutLP ?? [];
    }

    const filteredOutput = outputList.filter(({ metadata }) =>
      isBuyOrder ? !SundaeUtils.isAdaAsset(metadata) : true
    );

    const assetList = uniqBy(filteredOutput, ({ metadata }) => metadata.assetId);
    return stripActiveAsset(assetList, given?.metadata);
  }, [given?.metadata.assetId, assetsWithoutLP, balanceLoaded, poolsForInput, isBuyOrder]);

  const inputAssetList = useMemo(() => {
    const defaultAssetList: IMappedAsset<IAssetMetaData>[] = [
      { metadata: ADA_METADATA, quantity: new AssetAmount(0n, ADA_METADATA) },
      {
        metadata: SUNDAE_METADATA,
        quantity: new AssetAmount(0n, SUNDAE_METADATA),
      },
    ];
    const assetList = balanceLoaded ? assetsWithoutLP : defaultAssetList;

    return stripActiveAsset(assetList, taken?.metadata);
  }, [taken?.metadata, balanceLoaded, assetsWithoutLP]);

  return {
    givenAsset: searchAssetsBalance?.givenAssetBalance,
    takenAsset: searchAssetsBalance?.takenAssetBalance,
    balanceLoaded,
    assetsWithBalance: assetsWithoutLP,
    isFetched: Boolean(balanceLoaded && poolsForInput),
    isLoading: (!balanceLoaded && isWalletConnected) || isFetchingPoolsByAssetIds,
    isRefetching: balanceReloading || isRefetchingPoolsByAssetIds,
    inputAssetList,
    outputAssetList,
  };
};
